import { Component, type ErrorInfo, type ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
  /**
   * What to render when a descendant throws. Defaults to a small in-place
   * panel; pass `null` to swallow silently (useful for modal fallbacks
   * where the user can just close the modal).
   */
  fallback?: ((error: Error, reset: () => void) => ReactNode) | null;
  /**
   * Human-readable label for the bounded surface. Shown in the default
   * fallback ("Couldn't render the call panel.") and forwarded to the
   * console log so support reports can say which boundary caught it.
   */
  label?: string;
  /** Optional: invoked once on mount-after-catch so callers can clean up
   * external state (e.g. close the modal that hosts the boundary). */
  onReset?: () => void;
}

interface ErrorBoundaryState {
  error: Error | null;
}

// React error boundaries have to be class components — there is no hook
// equivalent for componentDidCatch yet.
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error(
      `[ErrorBoundary${this.props.label ? `:${this.props.label}` : ''}]`,
      error,
      info,
    );
  }

  reset = (): void => {
    this.setState({ error: null });
    this.props.onReset?.();
  };

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (this.props.fallback === null) return null;
    if (this.props.fallback) return this.props.fallback(error, this.reset);

    // Default fallback — a small inset panel. Doesn't grab the whole
    // viewport so the rest of the app stays usable.
    return (
      <div className="m-4 rounded-md border border-rose-500/30 bg-rose-500/5 p-4 text-xs">
        <p className="font-medium text-rose-300">
          Something broke{this.props.label ? ` in ${this.props.label}` : ''}.
        </p>
        <p className="text-zinc-400 mt-1">
          {error.message || 'No additional detail available.'}
        </p>
        <div className="mt-3 flex items-center gap-2">
          <button
            onClick={this.reset}
            className="rounded-md bg-zinc-900 hover:bg-zinc-800 hairline px-2.5 py-1 text-zinc-200 transition"
          >
            Try again
          </button>
          <button
            onClick={() => window.location.reload()}
            className="rounded-md text-zinc-500 hover:text-zinc-300 px-2.5 py-1 transition"
          >
            Reload the app
          </button>
        </div>
      </div>
    );
  }
}
